import { createContext, useContext, type ReactNode } from 'react';
import type { CollectionSummary } from '../utils/collectionSummary';

/**
 * Compact collection totals, published separately from `useAuth().user`.
 *
 * These totals change on every card add, so keeping them on the user object
 * meant the object was reallocated on every mutation and any effect keyed on
 * `user` re-subscribed its Firestore listeners. Identity, `teamId` and
 * visibility genuinely rarely change; this does not, so it lives apart.
 *
 * Fed by the same `users/{uid}` listener `AuthProvider` already holds — reading
 * it costs nothing extra.
 */
const CollectionSummaryContext = createContext<CollectionSummary | null>(null);

export function CollectionSummaryProvider({
  value,
  children,
}: {
  value: CollectionSummary | null;
  children: ReactNode;
}) {
  return (
    <CollectionSummaryContext.Provider value={value}>
      {children}
    </CollectionSummaryContext.Provider>
  );
}

/** Null until the first mutation or collection load. */
export function useCollectionSummary(): CollectionSummary | null {
  return useContext(CollectionSummaryContext);
}
